import React from 'react';
import { SelectFieldComponent } from '../../../../components/common/SelectFieldComponent';
import { MarcaService } from '../services/marcaService';
import { type ModeloListItem } from '../types/marca';

export interface ModeloSelectProps {
  /** Current selected value */
  value?: number | string;
  /** Change handler */
  onChange: (value: string | number) => void;
  /** ID of the marca to filter modelos */
  idMarca?: number | string;
  /** Field label */
  label?: string;
  /** Error message */
  error?: string;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Whether the field is required */
  required?: boolean;
  /** Placeholder text */
  placeholder?: string;
  /** Custom filter function to filter modelos */
  filter?: (modelo: ModeloListItem) => boolean;
  /** Custom sx prop for styling */
  sx?: any;
}

/**
 * ModeloSelect Component
 *
 * A reusable dropdown component that displays modelos filtered by marca.
 * Features:
 * - Automatic data loading based on selected marca
 * - Loading states and error handling
 * - Dynamic filtering by marca ID
 * - Additional custom filtering capabilities
 * - Full TypeScript support
 * - Consistent styling with other form components
 * - Automatic reset when marca changes
 */
export const ModeloSelect: React.FC<ModeloSelectProps> = ({
  value,
  onChange,
  idMarca,
  label = 'Modelo',
  error,
  disabled = false,
  required = false,
  placeholder,
  filter,
  sx,
}) => {
  const [modelos, setModelos] = React.useState<ModeloListItem[]>([]);
  const [loading, setLoading] = React.useState(false);

  // Fetch modelos when idMarca changes
  const fetchModelos = React.useCallback(async () => {
    if (!idMarca || idMarca === '') {
      setModelos([]);
      return;
    }

    try {
      setLoading(true);
      const marcaId = typeof idMarca === 'string' ? parseInt(idMarca) : idMarca;

      // Validate that marcaId is a valid number
      if (isNaN(marcaId) || marcaId <= 0) {
        console.warn('Invalid marca ID:', idMarca);
        setModelos([]);
        return;
      }

      const data = await MarcaService.getModelosByMarca(marcaId);
      setModelos(data);
    } catch (error) {
      console.error('Error fetching modelos by marca:', error);
      setModelos([]);
    } finally {
      setLoading(false);
    }
  }, [idMarca]);

  React.useEffect(() => {
    fetchModelos();
  }, [fetchModelos]);

  // Reset selected value when marca changes
  const prevIdMarcaRef = React.useRef<number | string | undefined>(idMarca);

  React.useEffect(() => {
    // Only reset if idMarca actually changed (not on initial mount)
    if (prevIdMarcaRef.current !== undefined && prevIdMarcaRef.current !== idMarca) {
      if (value && value !== '') {
        // Clear the selected modelo when marca changes
        onChange('');
      }
    }
    prevIdMarcaRef.current = idMarca;
  }, [idMarca, value, onChange]);

  // Filter modelos based on props
  const filteredModelos = React.useMemo(() => {
    let filtered = modelos;

    // Apply custom filter if provided
    if (filter) {
      filtered = filtered.filter(filter);
    }

    return filtered;
  }, [modelos, filter]);

  // Convert modelos to select options
  const options = React.useMemo(() => {
    return filteredModelos.map((modelo) => ({
      value: modelo.idModelo,
      label: modelo.descripcion,
    }));
  }, [filteredModelos]);

  // Handle error state
  const displayError = error;

  // Component is disabled if no marca is selected, it's explicitly disabled, or it's loading
  const isDisabled = disabled || loading || !idMarca || idMarca === '';

  return (
    <SelectFieldComponent
      label={required ? `${label} *` : label}
      value={value || ''}
      onChange={onChange}
      options={options}
      error={displayError}
      disabled={isDisabled}
      placeholder={placeholder || (!idMarca || idMarca === '' ? 'Selecciona una marca primero' : undefined)}
      loading={loading}
      sx={sx}
    />
  );
};

export default ModeloSelect;